import React from 'react';
import CombinedEditableSection from '../../../../../../components/CombinedEditableSection.comp';
import { parseInlineMarkdown } from '../../../../../utils/LearnTab/slideTextRenderer';
import { tUi } from '../../../../../../../../utils/dq_lc';
import {
  SlideTitle,
} from '../../../../../styles/LearnTab/slideBase.styles';
import {
  ActivityContainer,
  ActivityLeft,
  ActivityRight,
  FullActivityPill,
  ActivityContent,
  LinkInput,
  SubmitButton,
} from '../../../../../styles/LearnTab/activity.styles';
import { SlideTitleHeader } from '../common/SlideTitleHeader.comp';
import { renderTextWithId } from '../common/EditableSlideField.comp';

// A starter artifact may hold a table written as pipe-separated lines ("Metric | Unit | Threshold"). Two or more
// such lines in a row become a table (the first is the header, a "---|---" line is skipped); other lines stay text.
// Cells are shown as written, so a blank to fill in ("___") stays visible.
const cellsOf = (line) => line.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map((cell) => cell.trim());
const isSeparator = (cells) => cells.every((cell) => /^:?-{2,}:?$/.test(cell));
export const starterBlocks = (text) => {
  const blocks = [];
  String(text || '').split('\n').forEach((line) => {
    const cells = line.includes('|') ? cellsOf(line) : null;
    if (cells && cells.length >= 2) {
      if (isSeparator(cells)) return;
      const last = blocks[blocks.length - 1];
      if (last && last.type === 'table') last.rows.push(cells);
      else blocks.push({ type: 'table', rows: [cells], line });
    } else if (line.trim()) {
      blocks.push({ type: 'text', text: line });
    }
  });
  return blocks.map((block) => (block.type === 'table' && block.rows.length < 2 ? { type: 'text', text: block.line } : block));
};

// The table never grows past its box: fixed columns share the width and long words wrap; a table wider than
// four columns gets the whole slide width (see wideStarter), and the box scrolls sideways as a last resort.
const cellStyle = { padding: '6px 8px', border: '1px solid #cbd5e1', textAlign: 'left', verticalAlign: 'top',
  fontSize: '0.88rem', overflowWrap: 'anywhere', wordBreak: 'break-word' };
export const wideStarter = (text) => starterBlocks(text).some((block) => block.type === 'table' && block.rows[0].length > 4);
const StarterArtifact = ({ text }) => starterBlocks(text).map((block, idx) => (
  block.type === 'table' ? (
    <div key={idx} style={{ maxWidth: '100%', overflowX: 'auto', margin: '4px 0 10px' }}>
      <table data-starter-table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', background: '#ffffff' }}>
        <thead>
          <tr>{block.rows[0].map((cell, c) => <th key={c} style={{ ...cellStyle, background: '#e2e8f0' }}>{cell}</th>)}</tr>
        </thead>
        <tbody>
          {block.rows.slice(1).map((row, r) => (
            <tr key={r}>{block.rows[0].map((_, c) => <td key={c} style={cellStyle}>{row[c] || ''}</td>)}</tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <p key={idx} style={{ margin: '0 0 6px', fontStyle: 'italic', color: '#334155', lineHeight: 1.55, textAlign: 'left' }}>
      {parseInlineMarkdown(block.text)}
    </p>
  )
));

export const ShareWorkSlide = ({
  slide,
  highlightWord,
  isEditMode = false,
  slideEdits = {},
  showEnglishContent = false,
  onSaveEdit,
  onResetEdit,
  onCancelEdit,
  uiLocale = 'en-US',
}) => {
  const origin = slide?._pbdq_origin || slide || {};
  const slideType = (origin.slide_type || slide?._pbdq_kind || '').toLowerCase();
  const isDesignChallenge = slideType === 'design_challenge' || Boolean(origin.challenge_title || origin.challenge_brief);
  const challengeTitle = origin.challenge_title || slide?.SlideTitle || origin.title || origin.headline || '';
  const challengeBrief = origin.challenge_brief || slide?.SlideContent || '';

  const textRendererOptions = { slideEdits, showEnglishContent, highlightWord };

  if (isDesignChallenge) {
    return (
      <div style={{ width: '100%', maxWidth: '1400px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16, textAlign: 'left' }}>
        {/* 1. Pill matching JournalingActivity left-column width */}
        <FullActivityPill style={{ width: 'calc(50% - 10px)', maxWidth: '600px' }}>
          {tUi('slide.activity.shareWorkTime', uiLocale)}
        </FullActivityPill>

        {/* Title & Full-width Lead Brief */}
        <div style={{ textAlign: 'left' }}>
          {isEditMode ? (
            <CombinedEditableSection
              slide={slide}
              isEditMode={isEditMode}
              slideEdits={slideEdits}
              showEnglishContent={showEnglishContent}
              onSave={onSaveEdit}
              onReset={onResetEdit}
              onCancel={onCancelEdit}
              TitleComponent={SlideTitle}
              ContentComponent={ActivityContent}
            />
          ) : (
            challengeTitle ? (
              <SlideTitle style={{ marginTop: 8, marginBottom: 8, textAlign: 'left' }}>
                {highlightWord ? highlightWord('title', challengeTitle) : challengeTitle}
              </SlideTitle>
            ) : (
              <SlideTitleHeader
                slide={slide}
                slideEdits={slideEdits}
                showEnglishContent={showEnglishContent}
                highlightWord={highlightWord}
              />
            )
          )}

          {/* 2. "Create a 4-step..." expanded across both columns on top */}
          {challengeBrief && (
            <div style={{ fontSize: '1.08rem', lineHeight: 1.6, color: '#1f2937', fontWeight: 500, textAlign: 'left', marginTop: 4 }}>
              {parseInlineMarkdown(challengeBrief)}
            </div>
          )}
        </div>

        {/* 3. 2-Column Content Grid: Left = Starter Scenario, Right = Success Criteria */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 20, alignItems: 'stretch' }}>
          {/* Left Column: Starter Scenario (the whole width when its table has more than four columns) */}
          <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left', minWidth: 0,
                        gridColumn: wideStarter(origin.starter_artifact) ? '1 / -1' : undefined }}>
            {(origin.starter_artifact || (Array.isArray(origin.scenario_bullets) && origin.scenario_bullets.length > 0)) && (
              <div className="info-box" style={{ height: '100%', margin: 0, padding: '16px 20px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, textAlign: 'left', boxSizing: 'border-box' }}>
                <span className="label" style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, textAlign: 'left' }}>
                  {tUi('slide.shareWork.starterScenario', uiLocale)}
                </span>
                {origin.starter_artifact && <StarterArtifact text={origin.starter_artifact} />}
                {Array.isArray(origin.scenario_bullets) && origin.scenario_bullets.length > 0 && (
                  <ul style={{ paddingLeft: 18, margin: '8px 0 0', textAlign: 'left' }}>
                    {origin.scenario_bullets.map((sb, idx) => (
                      <li key={idx} style={{ marginBottom: 6, lineHeight: 1.5, color: '#475569', fontSize: '0.95rem', textAlign: 'left' }}>
                        {parseInlineMarkdown(sb)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Right Column: Success Criteria (below the starter, at full width, when the starter table is wide) */}
          <div style={{ display: 'flex', flexDirection: 'column', textAlign: 'left', minWidth: 0,
                        gridColumn: wideStarter(origin.starter_artifact) ? '1 / -1' : undefined }}>
            {Array.isArray(origin.success_criteria) && origin.success_criteria.length > 0 && (
              <div style={{ height: '100%', padding: '16px 20px', background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 1px 3px rgba(0,0,0,0.05)', textAlign: 'left', boxSizing: 'border-box' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '1.05rem', color: '#111827', marginBottom: 12, textAlign: 'left' }}>
                  {tUi('slide.shareWork.successCriteria', uiLocale)}
                </div>
                <ol style={{ paddingLeft: 20, margin: 0, textAlign: 'left' }}>
                  {origin.success_criteria.map((sc, idx) => (
                    <li key={idx} style={{ marginBottom: 8, lineHeight: 1.55, color: '#374151', fontSize: '0.98rem', textAlign: 'left' }}>
                      {parseInlineMarkdown(sc)}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>

        {/* 4. Content-fitted Tactical Tip Callout Box */}
        {origin.tactical_tip && (
          <div className="content-so-what" style={{ width: 'fit-content', maxWidth: '100%', boxSizing: 'border-box', margin: 0, padding: '12px 18px', background: '#ecfdf5', borderRadius: 10, borderLeft: '3px solid #10b981', color: '#065f46', fontWeight: 500, fontSize: '0.95rem', lineHeight: 1.55, textAlign: 'left' }}>
            💡 <strong style={{ color: '#047857' }}>{tUi('slide.shareWork.tacticalTip', uiLocale)}</strong> {parseInlineMarkdown(origin.tactical_tip)}
          </div>
        )}

        {/* 5. Content-fitted Submission Bar (Centered) */}
        <div style={{ width: 'fit-content', maxWidth: '100%', boxSizing: 'border-box', margin: '8px auto 0', padding: '12px 16px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <LinkInput
            variant="outlined"
            type="url"
            placeholder={tUi('slide.activity.shareLinkPlaceholder', uiLocale)}
            style={{ width: '480px', maxWidth: '100%', margin: 0 }}
          />
          <SubmitButton style={{ minWidth: 120, margin: 0 }}>{tUi('slide.activity.submit', uiLocale)}</SubmitButton>
        </div>
      </div>
    );
  }

  return (
    <ActivityContainer>
      <ActivityLeft>
        <FullActivityPill>{tUi('slide.activity.shareWorkTime', uiLocale)}</FullActivityPill>
        {isEditMode ? (
          <CombinedEditableSection
            slide={slide}
            isEditMode={isEditMode}
            slideEdits={slideEdits}
            showEnglishContent={showEnglishContent}
            onSave={onSaveEdit}
            onReset={onResetEdit}
            onCancel={onCancelEdit}
            TitleComponent={SlideTitle}
            ContentComponent={ActivityContent}
          />
        ) : (
          <>
            {challengeTitle ? (
              <SlideTitle>
                {highlightWord ? highlightWord('title', challengeTitle) : challengeTitle}
              </SlideTitle>
            ) : (
              <SlideTitleHeader
                slide={slide}
                slideEdits={slideEdits}
                showEnglishContent={showEnglishContent}
                highlightWord={highlightWord}
              />
            )}
            {renderTextWithId('content_0', slide?.SlideContent, ActivityContent, {}, textRendererOptions)}
          </>
        )}
      </ActivityLeft>
      <ActivityRight>
        <LinkInput
          variant="outlined"
          type="url"
          placeholder={tUi('slide.activity.shareLinkPlaceholder', uiLocale)}
        />
        <SubmitButton>{tUi('slide.activity.submit', uiLocale)}</SubmitButton>
      </ActivityRight>
    </ActivityContainer>
  );
};

export default ShareWorkSlide;
